import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import type { SourceBucket } from "../api";
import { SOURCE_COLOR } from "../constants";
import { fmtCents, fmtCount, fmtPct } from "../format";

/**
 * Pipeline-by-source breakdown — six sources, $ + count + % of total +
 * progress against goal. The static HTML uses colored dots; we reuse the
 * same palette through SOURCE_COLOR.
 */
export function PipelineBySourceCard({
  rows,
  goalCents,
}: {
  rows: SourceBucket[];
  goalCents?: number;
}) {
  const total = rows.reduce((a, r) => a + r.amount_cents, 0);
  const pct = goalCents && goalCents > 0 ? (total / goalCents) * 100 : null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
          Pipeline by Source
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-baseline gap-3">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">
            Total
          </span>
          <span className="text-2xl font-bold text-power tabular-nums">
            {fmtCents(total)}
          </span>
          {goalCents != null && (
            <span className="rounded border bg-muted px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
              Goal: {fmtCents(goalCents)}
            </span>
          )}
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Source</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Deals</TableHead>
              <TableHead className="text-right">% of Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const dot = SOURCE_COLOR[r.deal_source] ?? "hsl(var(--muted-foreground))";
              const pctOf = total > 0 ? (r.amount_cents / total) * 100 : 0;
              return (
                <TableRow key={r.deal_source}>
                  <TableCell>
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="inline-block h-2 w-2 rounded-full"
                        style={{ backgroundColor: dot }}
                      />
                      {r.deal_source}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {fmtCents(r.amount_cents)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">
                    {fmtCount(r.deal_count)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {fmtPct(pctOf, 1)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {pct != null && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Progress to goal</span>
              <span className="font-mono">{fmtPct(pct, 1)}</span>
            </div>
            <Progress value={Math.min(pct, 100)} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
